/*
 * Copyright (c) 2020
 *    Reliant Business Valuation, LLC - http://www.reliantvalue.com
 *    Author: Kirill Vizniuk - kirill@internalvaluation.com
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Owner } from './owner';
import { Location } from './location';
import { Product } from './product';
import { Customer } from './customer';
import { Competitor } from './competitor';
import { Contact } from './contact';
import { Franchise } from '../franchise';
import { Naics } from '../naics';
import { Sic } from '../sic';
import { CustomQuestion } from './custom.question';
import { RPi } from '../rpi';
import { RoT } from '../rot';
import { IBISReport } from '../ibis-report';
import { Rma } from '../rma';
import { TransactionFilter } from './transaction-filters';

export class BVQReport {
  ID: string;
  REPORT_ID: string;
  NAICS: Naics; // !!!!!! in Report
  SIC: Sic;
  FRANCHISE: Franchise; // +++++++++++++++
  COMPANY_ZIP: string; // Unsed - Moved into Location
  COMPANY_STATE: string; // Unsed - Moved into Location
  COMPANY_CITY: string; // Unsed - Moved into Location
  COMPANY_ADDRESS: string; // Unsed - Using Location's value

  COMPANY_MORE_LOCATIONS: boolean;

  // Ownership
  OWNERS: Owner[];
  GENERAL_MANAGER: string;
  GM_W2_ATTACHMENT: string;

  // Overview
  YEARS_IN_BUSINESS: string;
  // COMPANY_INCORPORATED: string; // Not in use
  HAS_SUBSIDIARY_COMPANIES: boolean;
  SUBSIDIARY_COMPANIES: string;
  SUBSIDIARY_COMPANIES_BUSINESS: string;
  IS_SUBSIDIARY_COMPANY: boolean;
  IS_SUBSIDIARY_DESCRIBE: string;
  PARENT_COMPANY: string;
  RESTRICTIONS_PENDING: boolean;
  RESTRICTIONS_DESCRIPTION: string;
  //IS_FRANCHISE: boolean;
  COMPANY_DOES: string;
  COMPANY_HISTORY: string;
  TOTAL_TEMPORARY_EMPLOYEE: number;

  // Management
  NUMBER_OF_EMPLOYEES: number;
  EMPLOYEES_RELATED_TO_SELLER: boolean;
  EMPLOYEES_RELATED_TO_SELLER_WAGE: boolean;
  EMPLOYEES_RELATED_RESPONSIBILITIES: string;
  EMPLOYEES_RELATED_HOURS_WORKED: number;
  EMPLOYEES_RELATED_SALARY: number;
  EMPLOYEES_RELATED_WAGE_STATEMENT: string;
  GENERAL_MANAGER_DISPLAY: boolean;

  // Locations
  LOCATIONS: Location[];

  // Products
  PRODUCTS: Product[];

  // Clients & Concentration
  CLIENTS_AMOUNT: number;
  CLIENTS_REVENUE: number;
  CLIENTS_FIFTEEN: string;
  CLIENT_CUSTOMERS: Customer[];

  // Competition
  COMPETITORS: Competitor[];

  // Financials
  SALES_INC_DEC: string;
  SEASONALITY: string;
  PROJECTED_REVENUE_CURRENT: string;
  PROJECTED_REVENUE_NEXT: string;

  // Inventory
  EQUIPMENT_AVG_AGE: number;
  PURCHASE_NEW_INV_REQUIRED: boolean;
  PURCHASE_NEW_INV_DESCRIPTION: string;

  // Expenses
  DISC_EXP: string; // YES / NO / NA
  EXP_PAID_BY_OWNER: string; // YES / NO / NA
  OWNER_BENEFITS: string; // YES / NO / NA
  LEASE_EXPENSES: string; // NA / CL / AL
  NON_RECURRING_EXP: boolean;
  NON_RECURRING_EXP_DESC: string;

  SOURCES: any[]; // +++++++++++++++
  ASK: any[]; // +++++++++++++++
  READ_ONLY: any[]; // +++++++++++++++

  CUSTOM: CustomQuestion[] = [];
  ANALYSIS: CustomQuestion[] = [];
  NAICS_CUSTOM: CustomQuestion[] = [];
  INDUSTRY: any;

  //Added
  QUESTION_CUSTOM: CustomQuestion[] = [];

  CONTACTS: Contact[];

  // NAICS
  RESEARCH_IBIS_LAST: number;
  RESEARCH_IBIS_NEXT: number;
  RPI: RPi;
  RPI_LOWER_RISK: boolean;
  IS_FRANCHISE:boolean;

  ADDITIONAL_SICS: Sic[]= [];
  //ROT
  ROT: RoT;
  IBIS: IBISReport;
  IBIS_DATE: string;

  LATEST_INDUSTRY_PULL: string;
  ASK_QUESTION_CONTENT: string;
  REPORT_INDUSTRY_FILTER_DS: TransactionFilter;
  REPORT_INDUSTRY_FILTER_BC: TransactionFilter;


  //RMA
  RMA : Rma;

  //helpers
  dateCreated: Date;


  constructor(value: any = {}) {
    Object.assign(this, value);

    if (value.FRANCHISE)
      this.FRANCHISE = Franchise.parse(value.FRANCHISE);

    if(value.NAICS)
      this.NAICS = new Naics(value.NAICS);

    if(value.SIC)
      this.SIC = new Sic(value.SIC);

    if(value.RPI)
      this.RPI = new RPi(value.RPI);

    if(value.ROT)
      this.ROT = new RoT(value.ROT);

    if (value.ADDITIONAL_SICS)
      this.ADDITIONAL_SICS = Sic.parseArray(value.ADDITIONAL_SICS);

    if(value.IBIS)
      this.IBIS = new IBISReport(value.IBIS);

    if(value.LOCATIONS)
      this.LOCATIONS = Location.parseArray(value.LOCATIONS);

    if(value.RMA)
      this.RMA = new Rma(value.RMA)

    if(value.REPORT_INDUSTRY_FILTER_DS)
      this.REPORT_INDUSTRY_FILTER_DS = TransactionFilter.parse(value.REPORT_INDUSTRY_FILTER_DS);
    else
      this.REPORT_INDUSTRY_FILTER_DS = null;

    if(value.REPORT_INDUSTRY_FILTER_BC)
      this.REPORT_INDUSTRY_FILTER_BC = TransactionFilter.parse(value.REPORT_INDUSTRY_FILTER_BC);
    else
    this.REPORT_INDUSTRY_FILTER_BC = null;
  }

  static parseArray(json: any[]): BVQReport[] {
    const arr = [];
    if (json) {
      for (const j of json) {
        if (j) {
          arr.push(new BVQReport(j));
        }
      }
    }

    return arr;
  }

  hasAsk(field: string): boolean {
    if(!this.ASK[field])
      return true;

    if(this.ASK[field]){
      if(this.ASK[field].VALUE){
        return true;
      }
    }

    return false;
  }
}
